const PROD_API_URL = 'https://backend.istatec.com';
const OVERRIDE = import.meta.env.VITE_API_BASE_URL as string | undefined;

/**
 * In dev we call `/api/...` and let Vite proxy it upstream — the deployed
 * CORS whitelist doesn't currently return `Access-Control-Allow-Origin` for
 * http://localhost:3000, so a direct browser fetch would be blocked.
 * In production we hit the backend directly (CORS allows https://istatec.com).
 */
export const API_BASE_URL = OVERRIDE ?? (import.meta.env.DEV ? '/api' : PROD_API_URL);

export interface IstatecResponse<
  T extends Record<string, unknown> | null = Record<string, unknown> | null,
> {
  status: 'success' | 'error';
  message: string;
  status_code: number;
  data: T;
  request_id: string;
}

interface ValidationDetail {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

interface ValidationErrorBody {
  detail?: ValidationDetail[];
}

/**
 * Best-effort parse of an `IstatecResponse` body, tolerating servers that
 * occasionally return a raw string / empty body on infra failures.
 */
export async function parseIstatecResponse(res: Response): Promise<IstatecResponse> {
  try {
    return (await res.json()) as IstatecResponse;
  } catch {
    return {
      status: 'error',
      message: 'Unexpected server response. Please try again.',
      status_code: res.status,
      data: null,
      request_id: '',
    };
  }
}

/**
 * FastAPI 422 responses use a `detail[]` array instead of the standard
 * `IstatecResponse` envelope. Normalize it to the same shape the UI expects.
 */
export async function parseValidationError(res: Response): Promise<IstatecResponse> {
  try {
    const body = (await res.json()) as ValidationErrorBody;
    const fields = body.detail
      ?.map((d) => d.loc?.[1])
      .filter((v): v is string => typeof v === 'string')
      .join(', ');
    return {
      status: 'error',
      message: fields ? `Validation error on: ${fields}` : 'Validation error',
      status_code: 422,
      data: null,
      request_id: '',
    };
  } catch {
    return {
      status: 'error',
      message: 'Validation error',
      status_code: 422,
      data: null,
      request_id: '',
    };
  }
}
